"use client";

import { signOut, useSession } from "next-auth/react";
import { usePathname } from "next/navigation";
import { type ReactNode, useEffect, useRef } from "react";

import { useCurrentSsoHint } from "@/context/current-sso-hint";
import { initiateZapierSignIn } from "@/lib/auth-signin";
import { isProduction, isStaging } from "@/lib/environment";

import { CurrentSsoHintProvider } from "./current-sso-hint-provider";

const SsoHintRedirect = () => {
  const { data: session } = useSession();
  const ssohint = useCurrentSsoHint();
  const pathname = usePathname();
  const hasSignedInRef = useRef(false);

  // Check if we're in an embed context
  const isEmbedContext = pathname.startsWith("/embed");

  // Watch for changes to the ssohint cookie.
  // If the cookie changes to "anonymous", sign the user in.
  // ⚠️ The ssohint cookie is not set in localhost or Vercel preview domains.
  useEffect(() => {
    if (ssohint === "anonymous") {
      // Skip auto sign-in for popup login routes and share pages
      if (
        pathname.startsWith("/login") ||
        pathname.startsWith("/embed/login") ||
        pathname.startsWith("/share")
      ) {
        return;
      }

      // If the user has a session, useEffect below is already handling re-auth
      if (session) {
        return;
      }

      // Don't fire again if we already initiated signin for this "anonymous" period,
      // e.g., user navigated to a different page
      if (hasSignedInRef.current) {
        return;
      }

      hasSignedInRef.current = true;
      initiateZapierSignIn("sso_hint");
      return;
    }

    hasSignedInRef.current = false;
  }, [ssohint, pathname, session]);

  useEffect(() => {
    // ssohint changing indicates a new session on zapier.com
    // we need to force user back through sign in flow to make sure we match that session
    // otherwise, they won't be able to log in to Agents
    if (
      (isProduction || isStaging) &&
      ssohint &&
      session?.ssoHint &&
      ssohint !== session.ssoHint
    ) {
      // For embed context, redirect to embed login instead of signing out to zapier.com
      if (isEmbedContext) {
        const embedId = new URLSearchParams(window.location.search).get(
          "embedId",
        );
        const redirectUrl = embedId
          ? `/embed/login?embedId=${embedId}`
          : "/embed/login";
        window.location.href = redirectUrl;
      } else {
        void signOut();
      }
    }
  }, [session?.ssoHint, ssohint, pathname, isEmbedContext]);

  return null;
};

export const SsoHintProvider = ({
  isPublicRoute,
  isAdminRoute,
  children,
}: {
  isPublicRoute?: boolean;
  isAdminRoute?: boolean;
  children: ReactNode;
}) => {
  return (
    <CurrentSsoHintProvider>
      {!isPublicRoute && !isAdminRoute && <SsoHintRedirect />}
      {children}
    </CurrentSsoHintProvider>
  );
};
